/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import { Table, TableBody, Typography } from '@mui/material';
import { EnhancedTableHead } from './EnhancedTableHead';

import { Row } from './TableRow';
import { useUserListData } from '../../data/DataProviders/UserListProvider';
import { ConfirmationPopUp } from '../ConfirmationPopUp/ConfirmationPopUp';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { isDataLoaded, UserRole } from '../../types';
import { DataIndicators } from '../DataIndicators';
import { useDispatch, useSelector } from 'react-redux';
import { Actions, Thunks } from '../../actions';
import { useToasts } from '../../data/DataProviders/ToastProvider';
import { Selectors } from '../../selectors';
import { getUserId, useAuthHelper, useAuthState } from '../AuthContextProvider';
import { sortObjectArrayNaturally } from '../../lib/sort-naturally';
import { HeadCell, Order } from '../../types/table';
import { stableSort, getComparator } from '../../lib/table-helpers';

const capitalize = (s: any): string => {
    if (typeof s !== 'string') return '';
    return s.charAt(0).toUpperCase() + s.slice(1);
};
const convertUserList = (userList: any): any => {
    const loadedUserList: any = userList && userList.length > 0 ? userList : [];
    const userlist: any = [];
    loadedUserList.forEach((user: any) => {
        const obj = {
            email: user.email ? user.email.toString().toLowerCase() : '',
            firstName: user.fName ? capitalize(user.fName) : '',
            lastName: user.lName ? capitalize(user.lName) : '',
            role: user.roles.length === 0 ? '--' : user.roles.length > 1 ? 'Multiple Roles' : user.roles[0].roleName,
            location:
                user.roles.length === 0 || user.roles[0].roleName === UserRole.Unassigned
                    ? '--'
                    : user.roles.length === 1 &&
                      (user.roles[0].roleName === UserRole.OrgAdmin || user.roles[0].roleName === UserRole.EatonAdmin)
                    ? 'All Locations'
                    : user.roles.length > 1
                    ? `${user.roles.length} locations`
                    : user.roles[0].siteName,
            roles: user.roles,
            status:
                user.roles.length === 1 && user.roles[0].roleName === UserRole.Unassigned
                    ? UserRole.Unassigned
                    : user.status,
            userId: user.userId,
        };
        userlist.push(obj);
    });
    return userlist;
};

export const TableDetails: React.FC = () => {
    const { t } = useLanguageLocale();
    const [order, setOrder] = React.useState<Order>('asc');
    const [orderBy, setOrderBy] = React.useState('');
    const [openDelete, setOpenDelete] = React.useState(false);
    const userList = useUserListData();
    const userloadedList = isDataLoaded(userList) ? userList.value : [];
    const isEmpty = isDataLoaded(userList) ? userList.value.length === 0 : false;
    const formatedUserList = convertUserList(userloadedList);
    const [deleteUserId, setDeleteUserId] = React.useState('');
    const [deleteUserEmail, setDeleteUserEmail] = React.useState('');
    const [deleteSiteIds, setDeleteSiteIds] = React.useState<string[]>([]);
    const [userRows, setUserRows] = React.useState<any[]>([]);
    const [deleteIndex, setDeleteIndex] = React.useState(-1);
    const dispatch = useDispatch();
    const { addToast } = useToasts();
    const authHelper = useAuthHelper();
    const authState = useAuthState();
    const userId = getUserId(authState) || '';
    const registrationState = useSelector(Selectors.Registration.registration);

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: any): void => {
        const isAsc = orderBy === property && order === 'asc';

        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
        const sortedValue = stableSort(userRows, getComparator(order, orderBy));
        setUserRows(sortedValue);
    };
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const sortByNameRoleWise = (role: string) =>
        sortObjectArrayNaturally(
            formatedUserList.filter((row: any) => row.role === role),
            [{ sortField: 'firstName' }]
        );

    const groupMultipleRole = sortByNameRoleWise('Multiple Roles');
    const groupEatonAdmin = sortByNameRoleWise(UserRole.EatonAdmin);
    const groupOrganizationAdmin = sortByNameRoleWise(UserRole.OrgAdmin);
    const groupLocationAdmin = sortByNameRoleWise(UserRole.LocationAdmin);
    const groupSiteUser = sortByNameRoleWise(UserRole.SiteUser);
    const groupUnassignedUser = sortByNameRoleWise(UserRole.Unassigned);
    const groupOtherUser = sortObjectArrayNaturally(
        formatedUserList.filter(
            (row: any) =>
                row.role !== 'Multiple Roles' &&
                row.role !== UserRole.EatonAdmin &&
                row.role !== UserRole.OrgAdmin &&
                row.role !== UserRole.Unassigned &&
                row.role !== UserRole.LocationAdmin &&
                row.role !== UserRole.SiteUser
        ),
        [{ sortField: 'firstName' }]
    );
    const rows = [
        ...groupMultipleRole,
        ...groupEatonAdmin,
        ...groupOrganizationAdmin,
        ...groupLocationAdmin,
        ...groupSiteUser,
        ...groupUnassignedUser,
        ...groupOtherUser,
    ];

    React.useEffect(() => {
        if (isDataLoaded(userList)) {
            setUserRows(rows);
            setOrderBy('');
        }
    }, [userloadedList]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        if (!registrationState.deleteUserLoading && registrationState.deleteUserSuccess) {
            const newArray = userRows.filter((x) => x.userId !== deleteUserId);
            setUserRows([...newArray]);
            setDeleteIndex(-1);
            setDeleteUserId('');
            setDeleteUserEmail('');
            addToast(t('USERLIST.DELETE_SUCCESS', { replace: { email: deleteUserEmail } }));
            dispatch(Actions.Registration.deleteUserUnmounted());
        }
    }, [registrationState.deleteUserLoading, registrationState.deleteUserSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleDelete = (value: {
        show: boolean;
        userId: string;
        siteIds: string[];
        index: number;
        userEmail: string;
    }): void => {
        setDeleteUserId(value.userId);
        setDeleteSiteIds(value.siteIds);
        setOpenDelete(value.show);
        setDeleteIndex(value.index);
        setDeleteUserEmail(value.userEmail);
    };
    const closePopUp = (): void => {
        setOpenDelete(false);
        setDeleteIndex(-1);
    };
    const deleteUser = (): void => {
        dispatch(Thunks.Registration.deleteUser(deleteUserId, deleteSiteIds, addToast, authHelper));

        setDeleteSiteIds([]);
        setOpenDelete(false);
    };
    const headCells: HeadCell[] = [
        { id: 'firstName', label: t('USERLIST.COL_USERNAME'), hideSorting: false },
        { id: 'role', label: t('USERLIST.COL_ROLE'), hideSorting: false },
        { id: 'location', label: t('USERLIST.COL_LOCATION'), hideSorting: false },
        { id: 'email', label: t('USERLIST.COL_EMAIL'), hideSorting: false },
        { id: 'status', label: t('USERLIST.COL_STATUS'), hideSorting: false },
    ];
    return (
        <>
            <DataIndicators
                errorMessage={t('DASHBOARD.NO_USER_DATA')}
                emptyMessage={t('DASHBOARD.NO_USER_DATA')}
                data={userList}
                emptyIcon="Person"
                isEmpty={isEmpty}
            />
            {isDataLoaded(userList) && !isEmpty && (
                <Table stickyHeader size="small">
                    <EnhancedTableHead
                        order={order}
                        orderBy={orderBy}
                        onRequestSort={handleRequestSort}
                        headCells={headCells}
                    />

                    <TableBody style={{ maxHeight: 800 }} key={'mainTable'} data-testid="user-data">
                        {stableSort(userRows, getComparator(order, orderBy)).map((row: any, index: number) => (
                            <Row
                                loggedInUser={userId}
                                key={row.userId}
                                row={row}
                                index={index}
                                deleteClick={handleDelete}
                                deleteIndex={deleteIndex}
                            ></Row>
                        ))}
                    </TableBody>
                </Table>
            )}
            {openDelete && (
                <ConfirmationPopUp
                    title={t('USERLIST.DELETEACCOUNT')}
                    body={
                        <Typography variant="subtitle2" style={{ marginBottom: 30 }}>
                            {t('USERLIST.DELETECONFIRMATION', { replace: { email: deleteUserEmail } })}
                        </Typography>
                    }
                    okButtonText={t('ACTIONS.DELETE')}
                    cancelButtonText={t('ACTIONS.CANCEL')}
                    show={openDelete}
                    closeDialog={closePopUp}
                    handleOk={(): void => {
                        deleteUser();
                    }}
                    width="xs"
                    okButtonShow={true}
                    cancelButtonsShow={true}
                ></ConfirmationPopUp>
            )}
        </>
    );
};
